import * as React from "react";
import { Check, Coins, Eye, Lock, Send } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";

/**
 * The spine of the product: five milestones from deposit to payout.
 *
 * One component, two voices. The brand watches its money leave escrow; the
 * creator watches money arrive. Same five states, same index, different words —
 * which is exactly why this is one component and not two. A second copy would
 * drift the first time a milestone is renamed on one side only.
 */
export type EscrowVoice = "brand" | "creator";

const ICONS = [Lock, Eye, Check, Send, Coins] as const;
const STEP_KEYS = ["held", "review", "approved", "published", "released"] as const;

/**
 * Deliverable status → milestone index.
 *
 * Note that `PUBLISHED` is index 3, not "done". It is the creator's *claim*
 * that a post is live; `VERIFIED` is that claim checked, and only `VERIFIED`
 * hands off to the payment clock. Nothing downstream may read `PUBLISHED` as
 * finished, and this map is the one place that distinction is written.
 *
 * `REVISION_REQUESTED` sits back at 1 with `SUBMITTED`: the work is in the
 * brand's court either way, and moving the rail backwards would be a lie about
 * progress rather than a description of it.
 */
export function escrowIndexForStatus(status: string | null | undefined): number {
  switch (status) {
    case "APPROVED":
      return 2;
    case "PUBLISHED":
      return 3;
    case "VERIFIED":
      return 4;
    case "PENDING":
    case "SUBMITTED":
    case "REVISION_REQUESTED":
    default:
      return 1;
  }
}

/**
 * A collaboration's rail position, across all its deliverables.
 *
 * Deliberately the LEAST advanced one, not the furthest. A collaboration with
 * one verified video and two untouched stories has not reached "Pago liberado",
 * and showing the optimistic end of the range would tell the creator they are
 * about to be paid for work they have not delivered.
 */
export function escrowIndexForDeliverables(
  deliverables: ReadonlyArray<{ status: string }>,
): number {
  if (deliverables.length === 0) return 0;
  return Math.min(...deliverables.map((d) => escrowIndexForStatus(d.status)));
}

export interface EscrowLedgerProps extends React.HTMLAttributes<HTMLDivElement> {
  voice?: EscrowVoice;
  /** 0–4. Use `escrowIndexForStatus` rather than deriving it at the call site. */
  current: number;
  /** Already formatted — `USD 350`. */
  amount?: string | null;
}

export function EscrowLedger({
  voice = "brand",
  current,
  amount,
  className,
  ...props
}: EscrowLedgerProps) {
  const { t } = useTranslation();

  return (
    <div className={cn("", className)} {...props}>
      {amount && (
        <div className="mb-4 flex items-center justify-between gap-3">
          <p className="adv-label">{t("marketplace.escrow.ledgerTitle")}</p>
          <span className="adv-mono text-fine font-semibold text-neutral-500">
            {amount}
          </span>
        </div>
      )}

      <ol className="flex items-start">
        {STEP_KEYS.map((key, i) => {
          const Icon = ICONS[i];
          const reached = i <= current;
          const isLast = i === STEP_KEYS.length - 1;

          return (
            <li
              key={key}
              className={cn("flex min-w-0 items-start", isLast ? "flex-none" : "flex-1")}
            >
              <div className="flex w-24 shrink-0 flex-col items-center gap-2">
                <span
                  className={cn(
                    "inline-flex size-[30px] items-center justify-center rounded-full border-2",
                    "transition-[background-color,border-color,color] duration-[400ms] ease-[cubic-bezier(.2,.8,.2,1)]",
                    reached
                      ? "border-steel-600 bg-steel-600 text-white"
                      : "border-neutral-200 bg-white text-neutral-400",
                  )}
                >
                  <Icon className="size-3.5" strokeWidth={2.6} />
                </span>
                <span
                  className={cn(
                    "text-center text-micro leading-tight",
                    i === current ? "font-bold" : "font-medium",
                    reached ? "text-neutral-900" : "text-neutral-400",
                  )}
                >
                  {t(`marketplace.escrow.${voice}.${key}`)}
                </span>
              </div>

              {!isLast && (
                <span className="relative mt-[14px] h-0.5 min-w-4 flex-1 overflow-hidden rounded-sm bg-[var(--escrow-line)]">
                  <span
                    className="absolute inset-0 origin-left bg-steel-600 transition-transform duration-500"
                    style={{ transform: `scaleX(${i < current ? 1 : 0})` }}
                  />
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
